import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import Seo from '@/components/Seo';
import { toast, Toaster } from 'sonner';
import { Sparkles, Check, Clock, Zap } from 'lucide-react';

const gbp = (p) => `£${((p || 0) / 100).toFixed(2)}`;

export default function TrialPage() {
  const { code } = useParams();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [promo, setPromo] = useState(null);
  const [state, setState] = useState('loading'); // loading | valid | expired | notfound
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/promo/${code}`)
      .then(({ data }) => { setPromo(data); setState(data.valid ? 'valid' : 'expired'); })
      .catch((e) => setState(e.response?.status === 404 ? 'notfound' : 'expired'));
  }, [code]);

  const startTrial = async () => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post('/billing/checkout', { origin_url: origin, tier: promo.tier, promo_code: code });
      if (data.url) window.location.href = data.url;
      else { toast.error('Could not start checkout'); setBusy(false); }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Could not start your trial');
      setBusy(false);
    }
  };

  const days = promo?.trial_days || 7;
  const label = promo?.tier_label || 'Pro+';
  const alreadyOnPlan = user && (user.subscription_tier === promo?.tier) && user.is_pro;

  return (
    <div className="min-h-[85vh] bg-slate-900 text-white relative overflow-hidden flex items-center">
      <Seo title={`${days}-Day Free ${label} Trial`} noindex />
      <Toaster position="top-center" richColors />
      <div className="absolute inset-0 tm-tag-stripe opacity-10" />
      <div className="relative max-w-2xl mx-auto px-6 py-16 w-full" data-testid="trial-page">
        <div className="inline-flex items-center gap-2 bg-brand text-slate-900 px-3 py-1 text-xs uppercase tracking-widest font-bold">
          <Sparkles size={14}/> Limited offer
        </div>

        {state === 'loading' && <p className="mt-8 font-mono text-slate-400">Loading offer…</p>}

        {state === 'notfound' && (
          <div className="mt-6" data-testid="trial-notfound">
            <h1 className="font-display font-semibold text-4xl uppercase">Offer not found</h1>
            <p className="text-slate-300 mt-3">This trial link isn't valid. Check the link or see our plans.</p>
            <Link to="/pricing" className="tm-btn mt-6 inline-flex">See plans</Link>
          </div>
        )}

        {state === 'expired' && (
          <div className="mt-6" data-testid="trial-expired">
            <h1 className="font-display font-semibold text-4xl uppercase">This offer has ended</h1>
            <p className="text-slate-300 mt-3">The free {days}-day {label} trial link has expired — but you can still start a standard trial from our pricing page.</p>
            <Link to="/pricing" className="tm-btn mt-6 inline-flex">See plans &amp; start a trial</Link>
          </div>
        )}

        {state === 'valid' && (
          <div className="mt-6">
            <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight">
              {days} days of TradesMate <span className="text-brand">{label}</span> — free
            </h1>
            <p className="text-slate-300 mt-4 text-lg">
              Unlock everything in {label}: shareable quote links with live view-tracking &amp; customer accept/decline, the AI estimator, branded PDFs, the Job Tracker, Quote Templates and Employee Hours.
            </p>

            <ul className="mt-6 space-y-2 text-slate-200">
              {['Full Pro+ access for ' + days + ' days, free', 'Then just ' + gbp(promo.price_pence) + '/month — cancel any time', 'No charge during your trial'].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check size={18} className="text-brand"/> {t}</li>
              ))}
            </ul>

            <div className="mt-8">
              {authLoading ? (
                <p className="font-mono text-slate-400">…</p>
              ) : !user ? (
                <div className="flex flex-wrap gap-3" data-testid="trial-auth-cta">
                  <Link to={`/register?next=/trial/${code}`} className="tm-btn tm-btn-lg"><Zap size={18}/> Create free account &amp; claim</Link>
                  <Link to={`/login?next=/trial/${code}`} className="tm-btn tm-btn-ghost tm-btn-lg text-white border-white/30">I already have an account</Link>
                </div>
              ) : alreadyOnPlan ? (
                <div data-testid="trial-already">
                  <p className="text-brand font-bold">You're already on {label} — enjoy! 🎉</p>
                  <button onClick={() => nav('/dashboard')} className="tm-btn mt-4">Go to dashboard</button>
                </div>
              ) : (
                <button data-testid="trial-start-btn" onClick={startTrial} disabled={busy} className="tm-btn tm-btn-lg">
                  <Zap size={18}/> {busy ? 'Starting…' : `Start my ${days}-day free ${label} trial`}
                </button>
              )}
              <p className="mt-4 font-mono text-xs text-slate-400 flex items-center gap-2">
                <Clock size={13}/> Offer ends {promo.expires_at ? new Date(promo.expires_at).toLocaleDateString('en-GB') : 'soon'} · one trial per account
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
